"use client"

import * as React from "react"
import { Puzzle, ExternalLink, Trash2, Search, Grid, Check, X } from "lucide-react"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AppIcon } from "@/components/desk/AppIcon"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"
import { App, Language, Widget } from "@/types/workspace"
import { toast } from "@/lib/toast"

interface MarketplaceViewProps {
  t: Record<string, string>
  lang: Language
  marketplaceApps: App[]
  globallyInstalledApps: string[]
  developerApps: App[]
  marketplaceWidgets?: Widget[]
  installedWidgets?: any[]
  widgetsConfig?: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  setAuthAppToShow?: (app: App) => void
  handleToggleGlobalApp: (appId: string, action?: "install" | "uninstall") => void
  handleToggleGlobalWidget?: (widgetId: string, action?: "install" | "uninstall") => void
  handleOpenApp?: (app: App) => void
}

export function MarketplaceView({
  t,
  lang,
  marketplaceApps,
  globallyInstalledApps,
  developerApps,
  marketplaceWidgets = [],
  installedWidgets = [],
  widgetsConfig = [],
  setWidgetsConfig,
  setAuthAppToShow,
  handleToggleGlobalApp,
  handleToggleGlobalWidget,
  handleOpenApp,
}: MarketplaceViewProps) {
  const [activeTab, setActiveTab] = React.useState<"apps" | "widgets">("apps")
  const [searchQuery, setSearchQuery] = React.useState("")
  const [appToUninstall, setAppToUninstall] = React.useState<App | null>(null)
  const [widgetToUninstall, setWidgetToUninstall] = React.useState<any | null>(null)

  const standardIds = React.useMemo(() => ["system-calendar", "system-weather", "system-calculator", "system-notes"], [])

  const allAvailableWidgets = React.useMemo(() => {
    const list: any[] = []
    marketplaceWidgets.forEach((mw) => {
      if (!standardIds.includes(mw.id) && !mw.isDefault && !list.some((w) => w.id === mw.id || w.slug === mw.slug)) {
        list.push(mw)
      }
    })
    widgetsConfig.forEach((cw) => {
      if (!standardIds.includes(cw.id) && !cw.isDefault && cw.type !== "system" && !list.some((w) => w.id === cw.id)) {
        list.push(cw)
      }
    })
    return list
  }, [marketplaceWidgets, widgetsConfig, standardIds])

  // Filtering
  const filteredApps = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    if (!query) return marketplaceApps
    return marketplaceApps.filter(
      (app) =>
        app.name.toLowerCase().includes(query) ||
        (app.description && app.description.toLowerCase().includes(query))
    )
  }, [marketplaceApps, searchQuery])

  const filteredWidgets = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    if (!query) return allAvailableWidgets
    return allAvailableWidgets.filter(
      (widget) =>
        widget.name.toLowerCase().includes(query) ||
        (widget.description && widget.description.toLowerCase().includes(query))
    )
  }, [allAvailableWidgets, searchQuery])

  const handleWidgetToggle = (widget: any, action: "install" | "uninstall") => {
    if (widget.isDefault || standardIds.includes(widget.id)) {
      toast.error("Este widget é padrão do sistema e não pode ser alterado.")
      return
    }

    if (action === "install") {
      if (handleToggleGlobalWidget) {
        handleToggleGlobalWidget(widget.id, "install")
      } else if (setWidgetsConfig) {
        setWidgetsConfig((prev) => {
          const exists = prev.some((w) => w.id === widget.id)
          if (exists) {
            return prev.map((w) => (w.id === widget.id ? { ...w, enabled: true, visible: true } : w))
          }
          return [...prev, { ...widget, enabled: true, visible: true }]
        })
        toast.success(`Widget "${widget.name}" instalado com sucesso!`)
      }
    } else {
      if (handleToggleGlobalWidget) {
        handleToggleGlobalWidget(widget.id, "uninstall")
      } else if (setWidgetsConfig) {
        setWidgetsConfig((prev) => prev.filter((w) => w.id !== widget.id))
        toast.success(`Widget "${widget.name}" desinstalado!`)
      }
    }
  }

  return (
    <div className="flex flex-col h-full bg-zinc-50 dark:bg-zinc-950 overflow-hidden font-sans">
      {/* Header estilo App Store com busca e abas */}
      <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 p-6 shrink-0 space-y-5">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-extrabold text-zinc-900 dark:text-white flex items-center gap-2">
              <Puzzle className="size-5 text-[#004EB1] dark:text-blue-400" />
              {t.marketplaceTitle || "Marketplace da Bold"}
            </h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Explore e instale aplicativos e widgets oficiais ou homologados pela Bold na sua conta corporativa.
            </p>
          </div>

          {/* Campo de Busca */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              placeholder={activeTab === "apps" ? "Buscar aplicativos..." : "Buscar widgets..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-xs rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Abas de Navegação (Aplicativos & Widgets) */}
        <div className="max-w-5xl mx-auto flex items-center gap-2 pt-1 border-t border-zinc-100 dark:border-zinc-800/80">
          <button
            onClick={() => setActiveTab("apps")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeTab === "apps"
                ? "bg-[#004EB1] text-white shadow-sm shadow-[#004EB1]/20"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
              }`}
          >
            <Grid className="size-3.5" />
            <span>Aplicativos</span>
          </button>

          <button
            onClick={() => setActiveTab("widgets")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeTab === "widgets"
                ? "bg-[#004EB1] text-white shadow-sm shadow-[#004EB1]/20"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
              }`}
          >
            <Puzzle className="size-3.5" />
            <span>Widgets</span>
          </button>
        </div>
      </div>

      {/* Corpo com scroll */}
      <div className="flex-1 p-6 pb-24 overflow-y-auto bg-zinc-50 dark:bg-zinc-950 scrollbar-thin">
        <div className="max-w-5xl mx-auto pb-12">
          {/* ABA 1: APLICATIVOS */}
          {activeTab === "apps" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {filteredApps.length === 0 ? (
                <div className="md:col-span-2 py-16 text-center text-xs text-zinc-400 dark:text-zinc-500 italic font-sans space-y-2">
                  <Grid className="size-8 mx-auto opacity-30" />
                  <p>{searchQuery ? `Nenhum aplicativo encontrado para "${searchQuery}".` : t.noMarketplaceApps || "Nenhum aplicativo disponível no marketplace."}</p>
                </div>
              ) : (
                filteredApps.map((app) => {
                  const isInstalled = globallyInstalledApps.includes(app.id) || (Boolean(app.slug) && globallyInstalledApps.includes(app.slug))
                  const isOwnDeveloperApp = developerApps.some((da) => da.id === app.id)

                  return (
                    <Card
                      key={app.id}
                      className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden flex flex-col justify-between hover:shadow-md transition-all"
                    >
                      <CardHeader className="pb-3 flex flex-row items-start gap-4 space-y-0">
                        <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="medium" />
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 truncate font-sans">
                              {app.name}
                            </CardTitle>
                            {isOwnDeveloperApp ? (
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-950/20 dark:border-purple-900/30 dark:text-purple-400">
                                {lang === "pt"
                                  ? "Meu App (Dev)"
                                  : lang === "es"
                                    ? "Mi App (Dev)"
                                    : "My App (Dev)"}
                              </span>
                            ) : app.type === "official" ? (
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-blue-50 border-blue-100 text-[#004EB1] dark:bg-blue-950/20 dark:border-blue-900/30 dark:text-blue-400">
                                Oficial Bold
                              </span>
                            ) : app.type === "private" ? (
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-950/20 dark:border-purple-900/30 dark:text-purple-400">
                                {lang === "pt" ? "Exclusivo" : lang === "es" ? "Exclusivo" : "Exclusive"}
                              </span>
                            ) : null}
                          </div>
                          <CardDescription className="text-xs font-sans line-clamp-2">
                            {app.description || "Sem descrição disponível."}
                          </CardDescription>
                        </div>
                      </CardHeader>

                      <CardContent className="pt-0 pb-4 px-6 flex justify-between items-center bg-zinc-50/50 dark:bg-zinc-950/20 border-t border-zinc-100 dark:border-zinc-850/50 h-12 shrink-0">
                        <span className="text-[10px] text-zinc-400 font-sans">
                          {isInstalled
                            ? lang === "pt"
                              ? "Instalado na conta"
                              : lang === "es"
                                ? "Instalado en cuenta"
                                : "Installed on account"
                            : lang === "pt"
                              ? "Disponível"
                              : lang === "es"
                                ? "Disponible"
                                : "Available"}
                        </span>

                        <div className="flex items-center gap-2">
                          {isInstalled && (
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              onClick={() => {
                                if (typeof handleOpenApp === "function") {
                                  handleOpenApp(app)
                                }
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-3 font-sans font-semibold cursor-pointer flex items-center gap-1 shadow-sm"
                            >
                              <ExternalLink className="size-3" />
                              <span>{lang === "pt" ? "Abrir" : lang === "es" ? "Abrir" : "Open"}</span>
                            </Button>
                          )}

                          <Button
                            type="button"
                            variant={isInstalled ? "outline" : "default"}
                            size="sm"
                            onClick={() => {
                              if (isInstalled) {
                                setAppToUninstall(app)
                              } else if (typeof setAuthAppToShow === "function") {
                                setAuthAppToShow(app)
                              } else {
                                handleToggleGlobalApp(app.id, "install")
                              }
                            }}
                            className={`text-xs h-7 px-3 font-sans font-semibold cursor-pointer ${isInstalled
                                ? "border-red-200 text-red-600 hover:bg-red-500/10 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/15"
                                : "bg-[#004EB1] hover:bg-[#003d8d] text-white"
                              }`}
                          >
                            {isInstalled ? t.uninstallBtn || "Desinstalar" : t.installBtn || "Instalar"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })
              )}
            </div>
          )}

          {/* ABA 2: WIDGETS */}
          {activeTab === "widgets" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {filteredWidgets.length === 0 ? (
                <div className="md:col-span-2 py-16 text-center text-xs text-zinc-400 dark:text-zinc-500 italic font-sans space-y-2">
                  <Puzzle className="size-8 mx-auto opacity-30" />
                  <p>{searchQuery ? `Nenhum widget encontrado para "${searchQuery}".` : "Nenhum widget adicional disponível no marketplace."}</p>
                </div>
              ) : (
                filteredWidgets.map((widget) => {
                  const isSystem = widget.type === "system" || widget.type === "official"
                  const isInstalled = widgetsConfig.some((w) => w.id === widget.id && w.enabled !== false) || installedWidgets.some((iw) => iw.widgetId === widget.id)

                  return (
                    <Card
                      key={widget.id}
                      className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden flex flex-col justify-between hover:shadow-md transition-all"
                    >
                      <CardHeader className="pb-3 flex flex-row items-start gap-4 space-y-0">
                        <div className="size-11 rounded-2xl border border-zinc-200/60 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0 text-xl shadow-xs">
                          <WidgetIcon id={widget.id} icon={widget.icon} name={widget.name} className="size-6 text-[#004EB1] dark:text-blue-400" />
                        </div>
                        <div className="min-w-0 space-y-1 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 truncate font-sans">
                              {widget.name}
                            </CardTitle>
                            <span
                              className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${isSystem
                                  ? "bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-950/20 dark:border-emerald-900/30 dark:text-emerald-400"
                                  : "bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-950/20 dark:border-purple-900/30 dark:text-purple-400"
                                }`}
                            >
                              {isSystem ? "Oficial Bold" : "Customizado"}
                            </span>
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
                              {widget.dimension === "large" ? "2x1 (Grande)" : "1x1 (Pequeno)"}
                            </span>
                          </div>
                          <CardDescription className="text-xs font-sans line-clamp-2">
                            {widget.description || "Sem descrição disponível."}
                          </CardDescription>
                        </div>
                      </CardHeader>

                      <CardContent className="pt-0 pb-4 px-6 flex justify-between items-center bg-zinc-50/50 dark:bg-zinc-950/20 border-t border-zinc-100 dark:border-zinc-850/50 h-12 shrink-0">
                        <span className="text-[10px] text-zinc-400 font-sans">
                          {isInstalled
                            ? "Instalado na conta"
                            : "Disponível para instalação"}
                        </span>

                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant={isInstalled ? "outline" : "default"}
                            size="sm"
                            onClick={() => {
                              if (isInstalled) {
                                setWidgetToUninstall(widget)
                              } else {
                                handleWidgetToggle(widget, "install")
                              }
                            }}
                            className={`text-xs h-7 px-3 font-sans font-semibold cursor-pointer ${isInstalled
                                ? "border-red-200 text-red-600 hover:bg-red-500/10 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/15"
                                : "bg-[#004EB1] hover:bg-[#003d8d] text-white"
                              }`}
                          >
                            {isInstalled ? "Desinstalar" : "Instalar Widget"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Confirmação de Desinstalação de Aplicativo */}
      {appToUninstall && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <Trash2 className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Desinstalar Aplicativo</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Confirmar desinstalação</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950 p-3 rounded-lg border border-zinc-150 dark:border-zinc-800 leading-relaxed">
              Tem certeza que deseja desinstalar o aplicativo <strong className="text-zinc-900 dark:text-white">{appToUninstall.name}</strong>? Ele continuará listado aqui no Marketplace e você poderá instalá-lo novamente a qualquer momento.
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAppToUninstall(null)}
                className="text-xs h-8 cursor-pointer font-sans"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  handleToggleGlobalApp(appToUninstall.id, "uninstall")
                  toast.success(`Aplicativo "${appToUninstall.name}" desinstalado com sucesso!`)
                  setAppToUninstall(null)
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-semibold cursor-pointer font-sans"
              >
                Confirmar Desinstalação
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Desinstalação de Widget */}
      {widgetToUninstall && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <Trash2 className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Desinstalar Widget</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Confirmar desinstalação</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950 p-3 rounded-lg border border-zinc-150 dark:border-zinc-800 leading-relaxed">
              Tem certeza que deseja desinstalar o widget <strong className="text-zinc-900 dark:text-white">{widgetToUninstall.name}</strong> do seu workspace?
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setWidgetToUninstall(null)}
                className="text-xs h-8 cursor-pointer font-sans"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  handleWidgetToggle(widgetToUninstall, "uninstall")
                  setWidgetToUninstall(null)
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-semibold cursor-pointer font-sans"
              >
                Confirmar Desinstalação
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
